import {InlineLink, SubNav, ThemeProvider, type ColorMode} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useMemo} from 'react'
import Markdown, {type Components} from 'react-markdown'

import {WebGLPage} from '@github-ui/brand-webgl'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {MobileOS, OS} from '@github-ui/get-os'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useDetectedMobileOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {CtaBanner} from './components/CtaBanner/CtaBanner'
import {Hero} from './components/Hero/Hero'
import {Features} from './components/Features/Features'
import {Highlight} from './components/Highlight/Highlight'
import {CardsSection} from './components/CardsSection/CardsSection'
import {Pricing} from './components/Pricing/Pricing'
import {
  createEmailHandoffSubmitter,
  EmailHandoffDialog,
  useEmailHandoffDialog,
  type EmailHandoffSubmit,
} from './components/MobileEmailHandoff/MobileEmailHandoff'

import {COLOR_MODE, getCopy} from './Index.data'

import Styles from './Index.module.css'

const PLAN_MARKDOWN_COMPONENTS_BASE: Components = {
  a: ({href, children}) => <InlineLink href={href ?? '#'}>{children}</InlineLink>,
  p: ({children}) => <>{children} </>,
}

const PLAN_FOOTNOTE_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

const PLAN_ACTIONS_MESSAGE_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

const PLAN_FEATURES_ITEM_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_STORYBOOK = (import.meta as any).env?.STORYBOOK

export const MOBILE_EMAIL_HANDOFF_FEATURE_FLAG = 'site_github_app_mobile_email_handoff'

export type MobileEmailHandoffDependencies = {
  platform: MobileOS
  submitEmail: EmailHandoffSubmit
}

interface GitHubAppGAPageRoutePayload {
  initialOS?: OS
  mobileEmailHandoffEndpoint?: string
}

export interface GitHubAppGAPageProps {
  initialOS?: GitHubAppGAPageRoutePayload['initialOS']
  mobileEmailHandoffEndpoint?: GitHubAppGAPageRoutePayload['mobileEmailHandoffEndpoint']
  mobileExperience?: MobileEmailHandoffDependencies
}

export function isMobileEmailHandoffProductionEnabled(featureEnabled: boolean, endpoint?: string): boolean {
  return featureEnabled && Boolean(endpoint?.trim())
}

export const GitHubAppGAPageContent = ({
  initialOS,
  mobileEmailHandoffEndpoint,
  mobileExperience,
}: GitHubAppGAPageProps) => {
  const copy = getCopy()
  const colorMode: ColorMode = COLOR_MODE ?? 'auto'

  const hasHighlight = isFeatureEnabled('site_github_app_ga_page_highlight')
  const detectedMobileOS = useDetectedMobileOS()
  const mobilePlatform = mobileExperience?.platform ?? detectedMobileOS
  const hasProductionMobileHandoff = isMobileEmailHandoffProductionEnabled(
    isFeatureEnabled(MOBILE_EMAIL_HANDOFF_FEATURE_FLAG),
    mobileEmailHandoffEndpoint,
  )
  const submitEmail = useMemo<EmailHandoffSubmit | undefined>(() => {
    if (mobileExperience) return mobileExperience.submitEmail
    if (hasProductionMobileHandoff && mobileEmailHandoffEndpoint) {
      return createEmailHandoffSubmitter(mobileEmailHandoffEndpoint)
    }
    return undefined
  }, [hasProductionMobileHandoff, mobileEmailHandoffEndpoint, mobileExperience])
  const mobileHandoff = mobilePlatform && submitEmail ? {platform: mobilePlatform, submitEmail} : undefined
  const {dialogRef, location: dialogLocation, openDialog, returnFocusRef} = useEmailHandoffDialog()

  return (
    <ThemeProvider colorMode={colorMode} className={Styles['GitHubAppGAPage']}>
      <WebGLPage {...(colorMode === 'auto' ? {} : {mode: colorMode})}>
        <SubNav className={Styles['GitHubAppGAPage__subNav']}>
          <SubNav.Heading
            href={copy.subNav.heading.href}
            {...getAnalyticsEvent({
              action: copy.subNav.heading.label,
              tag: 'a',
              context: 'subnav_heading',
              location: 'subnav',
            })}
          >
            {copy.subNav.heading.label}
          </SubNav.Heading>
          {copy.subNav.items.map((link, index) => (
            <SubNav.Link
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`link-${index}`}
              href={link.href}
              aria-current={link.isSelected ? 'page' : undefined}
              {...getAnalyticsEvent({action: link.label, tag: 'a', context: 'subnav', location: 'subnav'})}
            >
              {link.label}
            </SubNav.Link>
          ))}
        </SubNav>

        <Hero
          {...copy.hero}
          initialOS={initialOS}
          mobile={
            mobileHandoff
              ? {
                  ...copy.mobile.hero,
                  cta: {
                    label: copy.mobile.emailHandoff.triggerLabel,
                    onOpen: openDialog('hero'),
                  },
                }
              : undefined
          }
        />

        <Features {...copy.features} initialOS={initialOS} />

        {hasHighlight && (
          <>
            <div className={clsx(Styles['GitHubAppGAPage__spacer'], Styles['GitHubAppGAPage__spacer--desktopOnly'])} />
            <Highlight
              {...copy.highlight}
              mobileHandoff={
                mobileHandoff
                  ? {
                      label: copy.mobile.emailHandoff.triggerLabel,
                      onOpen: openDialog('highlight'),
                    }
                  : undefined
              }
            />
          </>
        )}

        <CardsSection {...copy.tools} location="tools" />

        <div className={clsx(Styles['GitHubAppGAPage__spacer'], Styles['GitHubAppGAPage__spacer--mobileOnly'])} />

        <Pricing
          {...copy.pricing}
          plans={copy.pricing.plans.map(plan => ({
            ...plan,
            footnote: plan.footnote ? (
              <Markdown components={PLAN_FOOTNOTE_MARKDOWN_COMPONENTS}>{plan.footnote}</Markdown>
            ) : undefined,
            actionsMessage: plan.actionsMessage
              ? {
                  ...plan.actionsMessage,
                  content: (
                    <Markdown components={PLAN_ACTIONS_MESSAGE_MARKDOWN_COMPONENTS}>
                      {plan.actionsMessage.content}
                    </Markdown>
                  ),
                }
              : undefined,
            features: {
              ...plan.features,
              items: plan.features.items.map(item => ({
                ...item,
                label: <Markdown components={PLAN_FEATURES_ITEM_MARKDOWN_COMPONENTS}>{item.label}</Markdown>,
              })),
            },
          }))}
        />

        <div className={Styles['GitHubAppGAPage__spacer']} />

        <CtaBanner
          {...copy.ctaBanner}
          mobileHandoff={
            mobileHandoff
              ? {
                  label: copy.mobile.emailHandoff.triggerLabel,
                  onOpen: openDialog('cta_banner'),
                }
              : undefined
          }
        />

        <CardsSection {...copy.resources} location="resources" />

        <div className={Styles['GitHubAppGAPage__spacer']} />
      </WebGLPage>

      {mobileHandoff ? (
        <EmailHandoffDialog
          copy={copy.mobile.emailHandoff}
          dialogRef={dialogRef}
          location={dialogLocation}
          platform={mobileHandoff.platform}
          returnFocusRef={returnFocusRef}
          submitEmail={mobileHandoff.submitEmail}
        />
      ) : null}
    </ThemeProvider>
  )
}

const GitHubAppGAPageWithRoutePayload = ({initialOS: initialOSOverride}: GitHubAppGAPageProps) => {
  const routePayload = useRoutePayload<GitHubAppGAPageRoutePayload | null>()

  return (
    <GitHubAppGAPageContent
      initialOS={initialOSOverride ?? routePayload?.initialOS}
      mobileEmailHandoffEndpoint={routePayload?.mobileEmailHandoffEndpoint}
    />
  )
}

export const GitHubAppGAPage = (props: GitHubAppGAPageProps) => {
  if (IS_STORYBOOK) return <GitHubAppGAPageContent {...props} />

  return <GitHubAppGAPageWithRoutePayload {...props} />
}
